import { type QueryClient } from "@tanstack/react-query";
import { Throttler } from "@tanstack/react-pacer";
import { projectQueryKeys } from "~/lib/projectQueryKeys";
import { providerQueryKeys } from "~/lib/providerQueryKeys";
import { hydrateRuntimeBridgeBootstrapEnvelope } from "../runtimeBridge";
import {
  hasSavedEnvironmentRegistryHydrated,
  listSavedEnvironmentRecords,
  type SavedEnvironmentRecord,
  useSavedEnvironmentRegistryStore,
  waitForSavedEnvironmentRegistryHydration,
} from "./catalog";
import { type EnvironmentConnection } from "./connection";

export type EnvironmentServiceLifecycleDependencies = {
  readonly createPrimaryEnvironmentConnection: () => EnvironmentConnection;
  readonly syncSavedEnvironmentConnections: (
    records: ReadonlyArray<SavedEnvironmentRecord>,
  ) => Promise<void>;
};

export function createEnvironmentServiceLifecycle(
  dependencies: EnvironmentServiceLifecycleDependencies,
) {
  type EnvironmentServiceState = {
    readonly queryClient: QueryClient;
    readonly queryInvalidationThrottler: Throttler<() => void>;
    refCount: number;
    stop: () => void;
  };

  let activeService: EnvironmentServiceState | null = null;
  let needsProviderInvalidation = false;

  function readActiveService(): EnvironmentServiceState | null {
    return activeService;
  }

  function notifyProviderInvalidation(): void {
    needsProviderInvalidation = true;
    void activeService?.queryInvalidationThrottler.maybeExecute();
  }

  function stopActiveService(): void {
    activeService?.stop();
    activeService = null;
  }

  function start(queryClient: QueryClient): () => void {
    if (activeService?.queryClient === queryClient) {
      activeService.refCount += 1;
      return () => {
        if (!activeService || activeService.queryClient !== queryClient) {
          return;
        }
        activeService.refCount -= 1;
        if (activeService.refCount === 0) {
          stopActiveService();
        }
      };
    }

    stopActiveService();
    needsProviderInvalidation = false;
    const queryInvalidationThrottler = new Throttler(
      () => {
        if (!needsProviderInvalidation) {
          return;
        }
        needsProviderInvalidation = false;
        void queryClient.invalidateQueries({ queryKey: providerQueryKeys.all });
        void queryClient.invalidateQueries({ queryKey: projectQueryKeys.all });
      },
      {
        wait: 100,
        leading: false,
        trailing: true,
      },
    );

    dependencies.createPrimaryEnvironmentConnection();
    void hydrateRuntimeBridgeBootstrapEnvelope().catch(() => undefined);

    const unsubscribeSavedEnvironments = useSavedEnvironmentRegistryStore.subscribe(() => {
      if (!hasSavedEnvironmentRegistryHydrated()) {
        return;
      }
      void dependencies.syncSavedEnvironmentConnections(listSavedEnvironmentRecords());
    });

    void waitForSavedEnvironmentRegistryHydration()
      .then(() => dependencies.syncSavedEnvironmentConnections(listSavedEnvironmentRecords()))
      .catch(() => undefined);

    activeService = {
      queryClient,
      queryInvalidationThrottler,
      refCount: 1,
      stop: () => {
        unsubscribeSavedEnvironments();
        queryInvalidationThrottler.cancel();
      },
    };

    return () => {
      if (!activeService || activeService.queryClient !== queryClient) {
        return;
      }
      activeService.refCount -= 1;
      if (activeService.refCount === 0) {
        stopActiveService();
      }
    };
  }

  return { readActiveService, notifyProviderInvalidation, stopActiveService, start };
}
